/**
 * A cue's start or end as a timecode (1.9.0): shown and typed in the file's own timecode (the media's start timecode
 * for an asset, 00:00:00:00 for a sequence), built on TimecodeField's entry rules (parseTimecodeEntry: keypad digits,
 * "1000" is 00:00:10:00) with the start-timecode offset. Values snap to frames; Enter commits, Esc reverts; leaving
 * the field commits a valid entry and reverts any other.
 */
import { useState } from 'react';
import type { Rational } from '@prism/contracts';
import { cueTimecode, parseCueTimecode } from './timecode';

export interface CueTimecodeFieldProps {
  /** The value in ticks of media (or sequence) time. */
  ticks: number;
  rate: Rational;
  /** The timecode at media time 0, in frames at `rate` (0 for a sequence). */
  startFrames: number;
  /** 1.9.1: the media's length, so a timecode after midnight in a recording that crosses it is read on the next day. */
  durationTicks?: number | null;
  /** Called with the typed time in ticks of media time, snapped to a frame. */
  onCommit(ticks: number): void;
  label: string;
  invalid?: boolean;
  readOnly?: boolean;
  testId?: string;
  onFocus?(): void;
}

export function CueTimecodeField({ ticks, rate, startFrames, durationTicks, onCommit, label, invalid, readOnly, testId, onFocus }: CueTimecodeFieldProps) {
  const shown = cueTimecode(ticks, rate, startFrames);
  const [text, setText] = useState<string | null>(null);
  const [bad, setBad] = useState(false);
  const commit = (): boolean => {
    if (text === null) return true;
    const t = parseCueTimecode(text, rate, startFrames, durationTicks);
    if (t == null) { setBad(true); return false; }
    setText(null);
    setBad(false);
    if (t !== ticks) onCommit(t);
    return true;
  };
  return (
    <input
      className={`input mono cue-tc ${bad || invalid ? 'invalid' : ''}`} value={text ?? shown} aria-label={label} aria-invalid={bad || invalid || undefined}
      readOnly={readOnly} spellCheck={false} inputMode="numeric" data-testid={testId}
      title={bad ? 'Type a timecode, e.g. 10:00:01:12 or 1000112' : `${label}: type a timecode (keypad digits work: 112 is 00:00:01:12)`}
      onFocus={(e) => { e.currentTarget.select(); onFocus?.(); }}
      onChange={(e) => { setText(e.target.value); setBad(false); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); if (commit()) e.currentTarget.select(); }
        else if (e.key === 'Escape') { if (text !== null) { e.preventDefault(); e.stopPropagation(); setText(null); setBad(false); } }
      }}
      onBlur={() => { if (!commit()) { setText(null); setBad(false); } }}
    />
  );
}
